import { useQuery } from "@tanstack/react-query";
import { zaloStatusApi, zaloStatusQueryKeys } from "../../shared/api/zaloStatus";
import type { ZaloLine, ZaloLineState } from "../../shared/api/zaloStatus";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Kết nối Zalo — năm dòng, không kiến trúc (Phase 5, phân tích §E).
 *
 * Chủ phòng khám không có mô hình "uỷ quyền OA" / "app identity" / "giấy phép phát hành" trong đầu,
 * và không cần có để biết tin nhắn nhắc lịch của mình có đi được hay không. Mỗi dòng là MỘT câu trả
 * lời từ server; không dòng nào lộ token, secret, hay chuyện sản phẩm nào giữ cái gì.
 *
 * Màn hình này CHỈ ĐỌC. Nút "Kết nối" thuộc về Addy Message — nơi chạy vòng OAuth — và dựng một nút
 * ở đây gọi vòng sang đó nghĩa là một credential lại có hai nơi ghi, đúng thứ Option C dựng ra để
 * chấm dứt.
 */

/** Ba trạng thái, không phải hai. Xem ghi chú ở UNKNOWN. */
const TONE: Record<ZaloLineState, string> = {
  OK: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  OFF: "bg-muted text-muted-foreground ring-border",
  // Cố ý KHÔNG phải màu đỏ. "Không hỏi được" không phải lỗi của phòng khám, và tô đỏ sẽ khiến họ đi
  // sửa một thứ không hỏng.
  UNKNOWN: "bg-amber-50 text-amber-700 ring-amber-600/20",
};

const LABEL: Record<ZaloLineState, string> = {
  OK: "Đã sẵn sàng",
  OFF: "Chưa",
  UNKNOWN: "Chưa rõ",
};

const ROWS: Array<{ key: keyof RowSource; title: string; hint: string }> = [
  {
    key: "oa",
    title: "Zalo OA",
    hint: "Tài khoản Zalo Official Account của phòng khám đã được kết nối.",
  },
  {
    key: "productionApp",
    title: "Ứng dụng Zalo (thật)",
    hint: "Ứng dụng trên developers.zalo.me mà phòng khám dùng để chạy thật.",
  },
  {
    key: "miniApp",
    title: "Mini App",
    hint: "Đủ điều kiện để phát hành Mini App lên Zalo.",
  },
  {
    key: "messaging",
    title: "Gửi tin nhắn",
    hint: "Kênh đã bật và có ít nhất một mẫu tin được Zalo duyệt.",
  },
  {
    key: "sandbox",
    title: "Ứng dụng thử nghiệm",
    hint: "Không bắt buộc. Dùng để chạy thử mà không đụng vào ứng dụng thật.",
  },
];

interface RowSource {
  oa: ZaloLine;
  productionApp: ZaloLine;
  miniApp: ZaloLine;
  messaging: ZaloLine;
  sandbox: ZaloLine;
}

export function ZaloStatusPage() {
  const q = useQuery({
    queryKey: zaloStatusQueryKeys.mine(),
    queryFn: () => zaloStatusApi.mine(),
  });

  return (
    <div>
      <PageHeader
        title="Kết nối Zalo"
        description="Tình trạng kết nối Zalo của phòng khám. Trang này chỉ để xem — việc khai báo và kết nối do quản trị viên Addy thực hiện."
      />

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      ) : q.isError ? (
        <Card className="max-w-3xl">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <p className="text-sm text-muted-foreground">
              Chưa đọc được tình trạng kết nối. Thử lại sau ít phút.
            </p>
            <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
              Thử lại
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid max-w-3xl gap-4">
          {q.data?.actionable && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4 text-sm text-amber-900">{q.data.actionable}</CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="divide-y p-0">
              {ROWS.map((row) => {
                const line = q.data?.[row.key];
                const state: ZaloLineState = line?.state ?? "UNKNOWN";
                return (
                  <div key={row.key} className="flex items-start justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{row.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{row.hint}</p>
                      {line?.detail && (
                        <p className="mt-1 truncate text-xs text-foreground/70">{line.detail}</p>
                      )}
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${TONE[state]}`}
                    >
                      {LABEL[state]}
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Nói ra khi một sản phẩm anh em không trả lời, thay vì để năm dòng "Chưa rõ" không có
              lời giải thích — người đọc sẽ tưởng phòng khám mình bị ngắt kết nối. */}
          {(q.data?.addyMessageReachable === false || q.data?.addySitesReachable === false) && (
            <p className="text-xs text-muted-foreground">
              Một số mục đang hiển thị “Chưa rõ” vì hệ thống chưa hỏi được
              {q.data?.addyMessageReachable === false ? " Addy Message" : ""}
              {q.data?.addyMessageReachable === false && q.data?.addySitesReachable === false
                ? " và"
                : ""}
              {q.data?.addySitesReachable === false ? " Addy Sites" : ""}. Đây không phải lỗi cấu
              hình của phòng khám.
            </p>
          )}

          <div>
            <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
              Tải lại
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
